// Eskom PARCC — command palette.
//
// Opens on `/` (or the header's Search widget) and searches two things: the
// screens the caller may reach (the SAME `canAccessRoute` filter the navigation
// uses, so it can never be a back door to a gated screen) and the transaction
// population already in the query cache.
//
// Mounted by BOTH shells. Legacy has no Search widget in its chrome, but the `/`
// shortcut and the palette itself work there too — the capability is not
// Lumen-only, only its entry point in the header is.
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { CornerDownLeft, Search } from 'lucide-react';
import { useAuth } from '../../../lib/auth';
import { canAccessRoute } from '../../../lib/permissions';
import { scopeTransactions } from '../../../lib/permissions';
import { useTransactions } from '../../../hooks';
import { formatRand } from '../../../lib/money';
import { APP_ROUTES, EXTRA_ROUTES } from '../routes';
import type { Transaction } from '../../../data/types';

const MAX_TX_RESULTS = 6;

/** Everything a transaction can be matched on, lower-cased once per row. */
function haystack(tx: Transaction): string {
  return [tx.referenceNo, tx.supplier, tx.division, tx.mechanism, tx.assignedToEmail, tx.dataset]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

export function CommandPalette({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): React.JSX.Element | null {
  const navigate = useNavigate();
  const { grant } = useAuth();
  const { data: transactions } = useTransactions();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // A fresh query each time it opens — a palette that remembers is a palette
  // that shows you yesterday's search.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      // Focus after paint so the caret lands reliably.
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open]);

  const screens = useMemo(() => {
    const q = query.trim().toLowerCase();
    const nav = APP_ROUTES.filter((r) => canAccessRoute(r.path, grant)).map((r) => ({
      key: r.path,
      label: r.lumenLabel ?? r.label,
      to: `/${r.path}`,
    }));
    // Settings is deep-linked rather than navigable, but it is the one screen a
    // person always wants to find by name (it holds the theme switch).
    const extra = EXTRA_ROUTES.filter((r) => r.path === 'settings').map((r) => ({
      key: r.path,
      label: r.title ?? r.path,
      to: `/${r.path}`,
    }));
    const all = [...nav, ...extra];
    return q ? all.filter((s) => s.label.toLowerCase().includes(q)) : all;
  }, [query, grant]);

  const txResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    // Scope to what this person may SEE before searching — the palette must not
    // reveal a transaction the register would hide.
    const visible = scopeTransactions(
      (transactions ?? []).filter((t) => !t.deleted),
      grant,
    );
    const out: Transaction[] = [];
    for (const tx of visible) {
      if (haystack(tx).includes(q)) out.push(tx);
      if (out.length >= MAX_TX_RESULTS) break;
    }
    return out;
  }, [query, transactions, grant]);

  const flat = useMemo(
    () => [
      ...screens.map((s) => ({ kind: 'screen' as const, to: s.to, label: s.label })),
      ...txResults.map((tx) => ({
        kind: 'tx' as const,
        to: `/transactions?ref=${encodeURIComponent(tx.referenceNo)}`,
        label: `${tx.referenceNo} ${tx.supplier ?? ''}`.trim(),
      })),
    ],
    [screens, txResults],
  );

  useEffect(() => setActive(0), [query]);

  if (!open) return null;

  function go(to: string): void {
    onClose();
    navigate(to);
  }

  function onKeyDown(e: React.KeyboardEvent): void {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (flat.length === 0 ? 0 : (i + 1) % flat.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (flat.length === 0 ? 0 : (i - 1 + flat.length) % flat.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = flat[active];
      if (target) go(target.to);
    }
  }

  let index = -1;

  return createPortal(
    <div
      className="lm-palette-wrap lm-scrim"
      onClick={onClose}
      role="presentation"
      style={{ zIndex: 250 }}
    >
      <div
        className="lm-palette"
        role="dialog"
        aria-modal="true"
        aria-label="Search"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <div className="lm-palette-field">
          <Search size={18} aria-hidden style={{ color: 'var(--faint)', flex: 'none' }} />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search transactions, suppliers, divisions — or jump to a screen…"
            aria-label="Search transactions or jump to a screen"
          />
          <kbd className="lumen-kbd">Esc</kbd>
        </div>
        <div className="lm-palette-list">
          {screens.length > 0 && <div className="lm-palette-group">Jump to</div>}
          {screens.map((s) => {
            index += 1;
            const i = index;
            return (
              <button
                key={s.key}
                type="button"
                className={`lm-palette-item${i === active ? ' is-active' : ''}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => go(s.to)}
              >
                <span className="ic" aria-hidden>
                  <CornerDownLeft size={14} />
                </span>
                {s.label}
              </button>
            );
          })}

          {txResults.length > 0 && <div className="lm-palette-group">Transactions</div>}
          {txResults.map((tx) => {
            index += 1;
            const i = index;
            const sub = [tx.dataset, tx.division, tx.mechanism].filter(Boolean).join(' · ');
            return (
              <button
                key={tx.id || tx.referenceNo}
                type="button"
                className={`lm-palette-item${i === active ? ' is-active' : ''}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => go(`/transactions?ref=${encodeURIComponent(tx.referenceNo)}`)}
              >
                <span style={{ minWidth: 0, flex: 1 }}>
                  <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <span className="tnum" style={{ fontWeight: 700, fontSize: 12 }}>
                      {tx.referenceNo}
                    </span>
                    <span
                      style={{
                        fontWeight: 600,
                        fontSize: 12.5,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {tx.supplier ?? '—'}
                    </span>
                  </span>
                  <span className="small" style={{ display: 'block', marginTop: 2 }}>
                    {sub || '—'}
                    {tx.value ? ` · ${formatRand(tx.value)}` : ''}
                  </span>
                </span>
              </button>
            );
          })}

          {flat.length === 0 && (
            <div className="lm-palette-empty">
              No matches. Try a reference, supplier or screen name.
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
